import type { Bookmark } from "@repo/fika-shared/src/loader/bookmark-schema.js"
import type { Story } from "@repo/fika-shared/src/loader/story-schema.js"
import { useConnectivitySignal } from "@solid-primitives/connectivity"
import { useNavigate } from "@solidjs/router"
import { sortBy } from "remeda"
import { createMemo, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { isValid } from "@/context/hanko"
import type { SearchResult } from "@/data/search-index"
import { createSearch } from "@/data/search-sync"
import { files } from "@/data/storage"
import { LinkButton } from "@/designSystem/components/button"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { ButtonDrawer } from "@/designSystem/patterns/button-drawer"
import { List } from "@/designSystem/patterns/list"
import { Navigation } from "@/designSystem/patterns/navigation"
import SearchForm from "@/designSystem/patterns/search-form"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createStoriesRouter,
  FILTER,
  type Filter,
} from "@/routes/stories-router"
import { StoryComponent } from "../../designSystem/patterns/story"
import { Settings } from "./story-settings"
import { Sync } from "./sync"

export function StoryList() {
  const { db } = useAppContext()

  return (
    <Show when={!db.story.empty()} fallback={<Settings topLevel />}>
      <Table />
    </Show>
  )
}

type Result = {
  searchResult?: SearchResult
  story: Story
  bookmark: Bookmark
}

function Table() {
  const { LL } = useI18nContext()
  const { db } = useAppContext()
  const navigate = useNavigate()
  const isOnline = useConnectivitySignal()
  const { session } = useAppContext()
  const hasValidSession = createMemo(() => isValid(session))
  const { paths, listParams } = createStoriesRouter()
  const searchResults = createSearch(() => listParams().query, "story")

  const results = createMemo<Result[]>(() => {
    const results = searchResults()

    if (results) {
      return results.flatMap((searchResult, i) => {
        const story = db.story.get(String(searchResult.id))
        if (!story) return []

        const bookmark = db.bookmark.findBy({
          story_id: String(searchResult.id),
        })
        if (!bookmark) return []

        return {
          searchResult: {
            order: i,
            highlights: searchResult.highlight,
          },
          story,
          bookmark,
        }
      })
    }

    return db.bookmark.all().flatMap(bookmark => {
      const story = db.story.get(bookmark.story_id)
      if (!story) return []

      return {
        bookmark,
        story,
      }
    })
  })

  function filterByFilter(story: Story, filter: Filter) {
    if (filter === "all") return true

    return files().has(story.id)
  }

  function filterByTag(bookmark: Bookmark, tag: string) {
    if (tag === "all") return true

    return db.tag.findBy({
      target_type: "bookmark",
      target_id: bookmark.id,
      tag,
    })
  }

  const listResults = createMemo(() => {
    const items = results().filter(
      item =>
        filterByFilter(item.story, listParams().filter as Filter) &&
        filterByTag(item.bookmark, listParams().tag),
    )

    return sortBy(
      items,
      [result => result.searchResult?.order ?? 0, "asc"],
      [result => result.bookmark.bookmarked_at ?? 0, "desc"],
      [result => result.story?.dc_created_at ?? 0, "desc"],
    )
  })

  const filterValues = createMemo(() => {
    const tagResults = results().filter(item =>
      filterByTag(item.bookmark, listParams().tag),
    )

    return {
      values: Object.values(FILTER).map(filter => ({
        ...filter,
        count: tagResults.filter(item =>
          filterByFilter(item.story, filter.value),
        ).length,
      })),
      value: listParams().filter,
    }
  })

  const tagValues = createMemo(() => {
    const allTags = Array.from(
      new Set(db.tag.filterBy({ target_type: "bookmark" }).map(tag => tag.tag)),
    )
    const filterResults = results().filter(item =>
      filterByFilter(item.story, listParams().filter),
    )
    const values = allTags.flatMap(tag => {
      const count = filterResults.filter(item =>
        filterByTag(item.bookmark, tag),
      ).length
      if (count === 0) return []

      return {
        value: tag,
        count,
      }
    })

    return {
      values: [
        {
          value: "all",
          count: filterResults.length,
        },
        ...values,
      ],
      value: listParams().tag,
    }
  })

  return (
    <MainLayout
      header={<Navigation hue="emerald" title="bookmarks" />}
      footer={
        <ButtonDrawer
          hue="emerald"
          leftActions={
            <Show when={isOnline() && hasValidSession()}>
              <Sync />
            </Show>
          }
          rightActions={
            <LinkButton
              hue="emerald"
              href="/bookmarks/add"
              iconName="plus-circle"
            >
              {LL().common.addBookmarks()}
            </LinkButton>
          }
        />
      }
    >
      <List
        list={listResults()}
        pagination={{
          path: page => paths({ page }).url,
          page: listParams().page,
        }}
        headerOptions={
          <SearchForm
            query={listParams().query}
            onChange={({ query, filter, tag }) => {
              navigate(
                paths({
                  filter: filter as Filter,
                  tag,
                  query,
                }).url,
              )
            }}
            tag={tagValues()}
            filter={filterValues()}
          />
        }
        content={result => (
          <StoryComponent
            story={result.story}
            searchResult={result.searchResult}
          />
        )}
        href={result => paths().story(result.story).url}
      />
    </MainLayout>
  )
}
